import { Banner } from "@dropbox/dig-components/banner";
import { Button, IconButton } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { CloseLine } from "@dropbox/dig-icons/dist/mjs/assets/ui-icon/line/close";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import {
  PAP_Select_Upsell,
  Select_Upsell,
} from "@mirage/analytics/events/types/select_upsell";
import { PAP_Shown_Upsell } from "@mirage/analytics/events/types/shown_upsell";
import i18n from "@mirage/translations";
import classNames from "classnames";

import { useCallback, useEffect, useMemo } from "react";
import styles from "./AddAppsUpsell.module.css";
import { AppsFacepile } from "./AppsFacepile";

type AddAppsUpsellProps = {
  onDismiss: () => void;
  onOpenAddApps: () => void;
  actionSurface?: string;
  actionElement?: string;
  isEmbedded?: boolean;
};

export const AddAppsUpsell = ({
  onDismiss,
  onOpenAddApps,
  actionSurface,
  actionElement,
  isEmbedded = false,
}: AddAppsUpsellProps) => {
  const { reportPapEvent } = useMirageAnalyticsContext();

  const loggingProps: Select_Upsell["properties"] = useMemo(
    () => ({
      upsellFeature: "dash_connectors",
      upsellFormat: "banner",
      actionSurface,
      actionElement,
    }),
    [actionElement, actionSurface],
  );

  useEffect(() => {
    reportPapEvent(PAP_Shown_Upsell(loggingProps));
  }, [reportPapEvent, loggingProps]);

  const onClick = useCallback(() => {
    onOpenAddApps();
    reportPapEvent(
      PAP_Select_Upsell({
        ctaName: "add_dash_connectors",
        ...loggingProps,
      }),
    );
  }, [onOpenAddApps, reportPapEvent, loggingProps]);

  const onClose = useCallback(() => {
    onDismiss();
    reportPapEvent(
      PAP_Select_Upsell({
        ctaName: "close",
        ...loggingProps,
      }),
    );
  }, [onDismiss, reportPapEvent, loggingProps]);

  return (
    <Banner
      type="attention"
      withLeftIcon={false}
      withCloseButton={isEmbedded ? i18n.t("close") : undefined}
      className={classNames(
        styles.addAppsUpsellBanner,
        isEmbedded && styles.embedded,
      )}
      onRequestClose={onClose}
    >
      <Box display="flex">
        <Banner.Message className={styles.addAppsUpsellMessage}>
          <Box>
            <div className={styles.appIconsContainer}>
              <AppsFacepile />
            </div>
            <Text className={styles.addAppsUpsellTitle}>
              {i18n.t("add_apps_upsell_banner_title")}
            </Text>
            <Text className={styles.addAppsUpsellSubtitle}>
              {isEmbedded
                ? i18n.t("add_apps_upsell_banner_subtitle_fss")
                : i18n.t("add_apps_upsell_banner_subtitle_dash")}
            </Text>
            {isEmbedded && (
              <Button variant="primary" size="small" onClick={onClick}>
                {i18n.t("open_dash")}
              </Button>
            )}
          </Box>
        </Banner.Message>
        {!isEmbedded && (
          <Box display="flex" alignItems="center" columnGap="Micro Large">
            <Button variant="filled" size="large" onClick={onClick}>
              {i18n.t("add_apps")}
            </Button>
            <IconButton
              variant="transparent"
              onClick={onClose}
              aria-label={i18n.t("close")}
            >
              <UIIcon src={CloseLine} />
            </IconButton>
          </Box>
        )}
      </Box>
    </Banner>
  );
};
